import { CurrentSite } from '@snap-types/actions/current-site';
import { Router } from '@ui/config/routes';
import { useGetSnapSiteConfigQuery } from '@ui/endpoints/get-snap-site-config';
import {
  snapSiteConfigActions,
  snapSiteConfigSelectors,
} from '@ui/features/snap-site-config/state';
import { CurrentUser, useCurrentUser } from '@ui/hooks/useCurrentUser';
import { useDispatch } from '@ui/hooks/useDispatch';
import { errorFlash } from '@ui/hooks/useFlash';
import { useSelector } from '@ui/hooks/useSelector';
import { NotFound } from '@ui/pages/NotFound';
import { isAxiosError } from 'axios';
import React, { useEffect, useRef } from 'react';
import { Navigate, useParams } from 'react-router';

type WithCurrentTeamProps = {
  children: React.ReactNode;
  fallback: React.ReactNode;
};

export function WithCurrentTeam({ children, fallback }: WithCurrentTeamProps) {
  const { currentUser } = useCurrentUser();
  const dispatch = useDispatch();

  const { teamCode } = useParams<{ teamCode: string }>();
  if (!teamCode) {
    throw new Error('Must have a team code.');
  }

  const previousTeamCodeRef = useRef<string | undefined>(undefined);

  const currentSite = useSelector(snapSiteConfigSelectors.selectCurrentSite);

  const selectedSite = findSiteFromTeamCode({
    teamCode,
    currentUser,
  });

  if (!selectedSite) {
    throw new Error('Site not found');
  }

  const teamUuid = currentUser.teams.find(
    (team) => team.teamCode.toLowerCase() === teamCode?.toLowerCase(),
  )?.uuid;

  if (teamCode && !teamUuid) {
    throw new Error('Team not found');
  }

  const currentSiteMatchesRoute =
    currentSite.uuid === selectedSite.uuid &&
    currentSite.code.toLowerCase() === selectedSite.code.toLowerCase() &&
    currentSite.currentTeam?.uuid === teamUuid &&
    currentSite.currentTeam?.teamCode.toLowerCase() === teamCode.toLowerCase();

  const snapSiteConfigQuery = useGetSnapSiteConfigQuery({
    siteCode: selectedSite.code,
    siteUuid: selectedSite.uuid,
    teamUuid,
  });

  useEffect(() => {
    // Clear config when site code changes (before new data loads)
    if (
      previousTeamCodeRef.current !== undefined &&
      previousTeamCodeRef.current !== teamCode
    ) {
      dispatch(snapSiteConfigActions.clearSnapSiteConfig());
    }
    previousTeamCodeRef.current = teamCode;

    if (snapSiteConfigQuery.isSuccess && snapSiteConfigQuery.data.site.code) {
      // Process the response to match the Store state
      const { site, team } = snapSiteConfigQuery.data;

      const siteConfigData = {
        ...site,
        currentTeam: team,
      };

      dispatch(snapSiteConfigActions.setSnapSiteConfig(siteConfigData));
    }
  }, [
    teamCode,
    snapSiteConfigQuery.isSuccess,
    snapSiteConfigQuery.data,
    dispatch,
  ]);

  if (snapSiteConfigQuery.isLoading && !snapSiteConfigQuery.data) {
    return fallback;
  }

  if (snapSiteConfigQuery.isError) {
    const error = snapSiteConfigQuery.error;

    if (isAxiosError(error) && error?.response?.status === 404) {
      return <NotFound />;
    }

    throw new Error('Error fetching current site!');
  }

  if (!snapSiteConfigQuery.isSuccess || !currentSiteMatchesRoute) {
    // This is the render cycle between the query succeeding and the
    // dispatched setSnapSiteConfig action loading the site/team data into the Redux store
    return fallback;
  }

  if (snapSiteConfigQuery.isSuccess) {
    if (!canAccessSite(currentUser, currentSite)) {
      const to = Router.path(Router.routes.root);
      const flash = errorFlash(
        `You don't have access to ${currentSite.code.toUpperCase()}`,
      );

      return <Navigate to={to} state={{ flash }} />;
    }

    return children;
  }

  return null;
}

function canAccessSite(user: CurrentUser, site: CurrentSite) {
  return user.teams.some((team) => team.site.code === site.code);
}

function findSiteFromTeamCode({
  teamCode,
  currentUser,
}: {
  teamCode: string;
  currentUser: CurrentUser;
}) {
  const team = currentUser.teams.find(
    (team) => team.teamCode.toLowerCase() === teamCode.toLowerCase(),
  );
  if (!team) {
    throw new Error(`Site for team ${teamCode} not found`);
  }
  return {
    uuid: team.site.uuid,
    code: team.site.code,
    name: team.site.name,
  };
}
